import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_PAGES, CAMPAIGN_ALIASES, SOLUTIONS, INDUSTRIES } from "../data/sitePages";
import seo from '../data/siteSeo.json';
import { applyPageMetadata } from '../lib/pageMetadata';
import blogMetadata from "../data/blogMetadata.json";
import { BLOG_POSTS } from "../data/sitePages";
import { EN_US_TEXT } from "../i18n/en-US";
import {
    DEFAULT_LOCALE,
    ENGLISH_LOCALE,
    canonicalizePath,
    getLocaleFromPathname,
    stripLocalePrefix,
    withLocale,
} from "../lib/locale";

const translatePT = (value) => {
    if (!value) return value;
    const key = String(value).replace(/\s+/g, " ").trim();
    return EN_US_TEXT[key] || value;
};

const SITE_ORIGIN = seo.origin;

const ensureMeta = (selector, create) => {
    let node = document.head.querySelector(selector);
    if (!node) {
        node = create();
        document.head.appendChild(node);
    }
    return node;
};

const setNamedMeta = (name, content) => {
    const node = ensureMeta(`meta[name="${name}"]`, () => {
        const meta = document.createElement("meta");
        meta.setAttribute("name", name);
        return meta;
    });
    node.setAttribute("content", content);
};

const setLink = (rel, href, attrs = {}) => {
    const selector = attrs.hreflang
        ? `link[rel="${rel}"][hreflang="${attrs.hreflang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
    const node = ensureMeta(selector, () => {
        const link = document.createElement("link");
        link.setAttribute("rel", rel);
        Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value));
        return link;
    });
    node.setAttribute("href", href);
};

const canonicalBaseFor = pathname => {
    const path = canonicalizePath(stripLocalePrefix(pathname)).replace(/\/$/, '') || '/';
    if (path === '/products/tasks') return '/products/flow';
    return CAMPAIGN_ALIASES[path.slice(1)] ? '/' + CAMPAIGN_ALIASES[path.slice(1)] : path;
};

export const canonicalPathFor = (pathname) => {
    const locale = getLocaleFromPathname(pathname);
    const basePath = canonicalBaseFor(pathname);
    const localized = withLocale(basePath, locale);
    return localized === `/${locale}` ? `/${locale}/` : localized.replace(/\/$/, "");
};

export const metaForPath = (pathname) => {
    const locale = getLocaleFromPathname(pathname);
    const path = canonicalBaseFor(pathname);
    const [, group, slug] = path.split("/");
    const routeKey = path.slice(1);
    const routePage = ROUTE_PAGES[routeKey];
    if (group === "blog" && slug) {
        const post = blogMetadata.find(item => item.slug === slug);
        const index = locale === ENGLISH_LOCALE ? 1 : 0;
        const legacy = BLOG_POSTS.find(item => item.slug === slug);
        if (legacy) return { title: `${index ? translatePT(legacy.title) : legacy.title} - Zild`, description: index ? translatePT(legacy.excerpt) : legacy.excerpt };
        return post ? { title: `${post.title[index]} - Zild`, description: post.excerpt[index] } : {
            notFound: true,
            title: locale === ENGLISH_LOCALE ? "Article not found - Zild" : "Artigo não encontrado - Zild",
            description: locale === ENGLISH_LOCALE ? "Explore the Zild blog." : "Explore o Blog Zild.",
        };
    }

    const index = locale === ENGLISH_LOCALE ? 1 : 0;
    const localized = seo.pages[path];
    if (localized) return { title: localized.title[index], description: localized.description[index] };

    if (routePage) {
        const item = (group === 'solutions' ? SOLUTIONS : group === 'industries' ? INDUSTRIES : []).find(item => item.key === slug);
        const sourceTitle = item?.shortTitle || routePage.title;
        const localizedTitle = locale === ENGLISH_LOCALE ? translatePT(sourceTitle) : sourceTitle;
        const title = group === 'industries' && item?.shortTitle
            ? `${index ? 'AI for' : 'IA para'} ${localizedTitle}` : localizedTitle;
        const description = locale === ENGLISH_LOCALE ? translatePT(routePage.summary) : routePage.summary;
        return {
            title: `${title.replace(/[.]$/, '')} | Zild`,
            description,
        };
    }

    return { notFound: true, title: locale === ENGLISH_LOCALE ? 'Page not found | Zild' : 'Página não encontrada | Zild', description: locale === ENGLISH_LOCALE ? 'Explore Zild products, documentation and AI guides.' : 'Explore os produtos, a documentação e os guias de IA da Zild.' };
};

const RouteMeta = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        // A noindex error route must not leak into the next Docs page.
        setNamedMeta("robots", "index, follow");
        // Docs owns article metadata after loading its source page.
        if (/^\/docs(?:\/|$)/.test(stripLocalePrefix(pathname))) return undefined;
        const timer = window.setTimeout(() => {
            const routeMeta = metaForPath(pathname);
            const missing = Boolean(routeMeta.notFound || document.querySelector('[data-page-not-found]'));
            const english = getLocaleFromPathname(pathname) === ENGLISH_LOCALE;
            const meta = missing ? {
                title: english ? "Page not found - Zild" : "Página não encontrada - Zild",
                description: english ? "Explore Zild products, documentation and updates." : "Explore os produtos, a documentação e as novidades Zild.",
            } : routeMeta;
            setNamedMeta("robots", missing ? "noindex, follow" : "index, follow");
            const canonicalPath = canonicalPathFor(pathname);
            const canonical = `${SITE_ORIGIN}${canonicalPath}`;
            const alternateBasePath = canonicalBaseFor(pathname);
            const ptPath = withLocale(alternateBasePath, DEFAULT_LOCALE);
            const enPath = withLocale(alternateBasePath, ENGLISH_LOCALE);
            const ptHref = `${SITE_ORIGIN}${ptPath === `/${DEFAULT_LOCALE}` ? `/${DEFAULT_LOCALE}/` : ptPath}`;
            const enHref = `${SITE_ORIGIN}${enPath === `/${ENGLISH_LOCALE}` ? `/${ENGLISH_LOCALE}/` : enPath}`;

            applyPageMetadata(english ? ENGLISH_LOCALE : DEFAULT_LOCALE, meta, canonical,
                canonicalBaseFor(pathname).startsWith('/blog/') && !missing ? 'article' : 'website');
            setLink("canonical", canonical);
            setLink("alternate", ptHref, { hreflang: DEFAULT_LOCALE });
            setLink("alternate", enHref, { hreflang: ENGLISH_LOCALE });
            setLink("alternate", enHref, { hreflang: "x-default" });
        }, 60);

        return () => window.clearTimeout(timer);
    }, [pathname]);

    return null;
};

export default RouteMeta;
